import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/cn';
import { saveFailureMessage } from '@/lib/apiErrors';
import { parseTypedAmount } from '@/lib/number';
import { todayInPH } from '@/lib/phDate';
import { cropDayLabel, docOn, type Crop } from '@/lib/crop';
import { useAuth } from '@/hooks/useAuth';
import { useMembership } from '@/hooks/useMembership';
import { Dialog } from '@/components/ui/Dialog';
import type { SurvivalCheckInfo } from '@/types/generated/openapi';
import { EmptyNote, Section } from './shared';
import { headerActionClass } from './format';
import { canEditSurvivalCheck, useFarmSurvivalSampleSize, useSurvivalCheckMutations } from './survivalChecks';

// The default count for a new check. Each record keeps its own sample size.

export function SurvivalCheckCard({ farmId, crop }: { farmId: string; crop: Crop }) {
  const { user } = useAuth();
  const { isSupervisorOrAbove } = useMembership();
  const [dialog, setDialog] = useState<'new' | 'edit' | null>(null);
  const check = crop.survival_check ?? null;
  const open = crop.status === 'active';
  // A closed crop only shows a check that exists, read-only.
  if (!check && !open) return null;

  const pct = check?.survival_pct !== null && check?.survival_pct !== undefined ? Number(check.survival_pct) : null;
  const canEdit = !!check && open && canEditSurvivalCheck(check, isSupervisorOrAbove, user?.id);
  return (
    <Section labelledBy="ov-survival" title="Survival check">
      {check ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold tabular-nums text-gray-900">{pct !== null ? `${Number(pct.toFixed(1))}%` : '—'}</span>
            <span className="text-sm text-gray-600 tabular-nums">
              {check.survivors.toLocaleString()} of {check.sample_size.toLocaleString()} survived
            </span>
          </div>
          {pct !== null && (
            <div className="h-2 overflow-hidden rounded-full bg-gray-100" role="img" aria-label={`${pct.toFixed(1)}% survived`}>
              <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }} />
            </div>
          )}
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-gray-500">Checked</dt>
            <dd className="tabular-nums">
              {format(parseISO(check.check_date), 'MMM d')}
              {cropDayLabel(crop, check.check_date) ? ` · ${cropDayLabel(crop, check.check_date)}` : ''}
            </dd>
            {check.notes && (
              <>
                <dt className="text-gray-500">Notes</dt>
                <dd className="break-words">{check.notes}</dd>
              </>
            )}
          </dl>
          {open && (
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <button type="button" onClick={() => setDialog('edit')} className={cn(headerActionClass(), 'h-11')}>
                  Edit
                </button>
              )}
              <button type="button" onClick={() => setDialog('new')} className={cn(headerActionClass(), 'h-11')}>
                Record another
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-start gap-3">
          <EmptyNote>Not recorded yet. Record the survivors counted in a sample.</EmptyNote>
          <button type="button" onClick={() => setDialog('new')} className={cn(headerActionClass(true), 'h-11')}>
            Record survival check
          </button>
        </div>
      )}
      {dialog && (
        <SurvivalCheckDialog
          farmId={farmId}
          crop={crop}
          existing={dialog === 'edit' ? check : null}
          canDelete={isSupervisorOrAbove}
          onClose={() => setDialog(null)}
        />
      )}
    </Section>
  );
}

function SurvivalCheckDialog({
  farmId,
  crop,
  existing,
  canDelete,
  onClose,
}: {
  farmId: string;
  crop: Crop;
  existing: SurvivalCheckInfo | null;
  canDelete: boolean;
  onClose: () => void;
}) {
  const { create, update, remove } = useSurvivalCheckMutations(farmId);
  const today = todayInPH();
  const [date, setDate] = useState(existing?.check_date ?? today);
  // A new check starts with the farm's own sample size when the owner set one (else the field is empty and asks);
  // it stays editable here, and what is typed replaces it for good.
  const farmSampleSize = useFarmSurvivalSampleSize(farmId);
  const [sizeEdit, setSizeEdit] = useState<string | null>(null);
  const size = sizeEdit ?? (existing ? String(existing.sample_size) : farmSampleSize === null ? '' : String(farmSampleSize));
  const [survivors, setSurvivors] = useState(existing ? String(existing.survivors) : '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const sizeParsed = parseTypedAmount(size, { integer: true });
  const survivorsParsed = parseTypedAmount(survivors, { integer: true });
  const sizeValue = 'value' in sizeParsed ? sizeParsed.value : null;
  const survivorsValue = 'value' in survivorsParsed ? survivorsParsed.value : null;
  const sizeProblem =
    'error' in sizeParsed ? sizeParsed.error : sizeValue === null ? 'Enter the sample size.' : sizeValue <= 0 ? 'More than 0.' : null;
  const survivorsProblem =
    'error' in survivorsParsed
      ? survivorsParsed.error
      : survivorsValue === null
        ? 'Enter how many survived.'
        : sizeValue !== null && sizeValue > 0 && survivorsValue > sizeValue
          ? `Can't be more than the sample of ${sizeValue.toLocaleString()}.`
          : null;
  const valid = !sizeProblem && !survivorsProblem && !!date && date <= today && (!crop.stocking_date || date >= crop.stocking_date);
  const live = valid && sizeValue && survivorsValue !== null ? (survivorsValue * 100) / sizeValue : null;
  const doc = docOn(crop, date);
  const busy = create.isPending || update.isPending || remove.isPending;
  const error = create.error ?? update.error;

  const submit = () => {
    if (!valid || sizeValue === null || survivorsValue === null) return;
    const body = { check_date: date, sample_size: sizeValue, survivors: survivorsValue, notes: notes.trim() || null };
    if (existing) update.mutate({ id: existing.id, body }, { onSuccess: onClose });
    else create.mutate({ cycle_id: crop.id, ...body }, { onSuccess: onClose });
  };

  const field = 'min-h-11 w-full rounded-lg border border-gray-300 px-3 text-sm';
  return (
    <Dialog open onClose={onClose} title={existing ? 'Edit survival check' : 'Survival check'} size="md">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
            Check date
            <input type="date" required className={field} value={date} min={crop.stocking_date ?? undefined} max={today} onChange={(e) => setDate(e.target.value)} />
          </label>
          <span className="min-h-4 text-xs text-gray-500">
            {doc !== null ? `DOC ${doc}` : ''}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
              Sample size
              <input
                inputMode="numeric"
                required
                aria-invalid={sizeProblem ? true : undefined}
                className={cn(field, 'tabular-nums', sizeProblem && 'border-red-500')}
                value={size}
                onChange={(e) => setSizeEdit(e.target.value)}
              />
            </label>
            <span className={cn('min-h-4 text-xs', sizeProblem ? 'text-red-700' : 'text-gray-500')}>{sizeProblem ?? 'Pieces counted'}</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
              Survivors
              <input
                inputMode="numeric"
                required
                aria-invalid={survivorsProblem && survivors !== '' ? true : undefined}
                className={cn(field, 'tabular-nums', survivorsProblem && survivors !== '' && 'border-red-500')}
                value={survivors}
                onChange={(e) => setSurvivors(e.target.value)}
                placeholder="e.g. 462"
              />
            </label>
            <span className={cn('min-h-4 text-xs', survivorsProblem && survivors !== '' ? 'text-red-700' : 'text-gray-500')}>
              {survivors === '' ? 'Alive in the sample' : (survivorsProblem ?? '')}
            </span>
          </div>
        </div>
        <p className="min-h-5 text-sm font-semibold text-gray-900 tabular-nums" aria-live="polite">
          {live !== null ? `Survival ${Number(live.toFixed(1))}%` : ''}
        </p>
        <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
          Notes
          <input className={field} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        </label>
        {error && (
          <p role="alert" className="text-sm text-red-700">
            Not saved: {saveFailureMessage(error, 'try again')}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {existing && canDelete ? (
            confirmingDelete ? (
              <span className="flex items-center gap-2 text-sm">
                Delete this check?
                <button type="button" disabled={busy} onClick={() => remove.mutate(existing.id, { onSuccess: onClose })} className={headerActionClass()}>
                  Yes, delete
                </button>
                <button type="button" onClick={() => setConfirmingDelete(false)} className={headerActionClass()}>
                  No
                </button>
              </span>
            ) : (
              <button type="button" onClick={() => setConfirmingDelete(true)} className="text-sm font-semibold text-red-700 hover:underline">
                Delete
              </button>
            )
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className={cn(headerActionClass(), 'h-11')}>
              Cancel
            </button>
            <button type="submit" disabled={!valid || busy} className={cn(headerActionClass(true), 'h-11 disabled:opacity-50')}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
